// ─── Log Levels (syslog-compatible, matches GELF) ───

export const LogLevel = {
  Emergency: 0,
  Alert: 1,
  Critical: 2,
  Error: 3,
  Warning: 4,
  Notice: 5,
  Info: 6,
  Debug: 7,
} as const

export type LogLevelValue = typeof LogLevel[keyof typeof LogLevel]

// ─── Source Context (auto-populated per layer) ───

export interface LogSource {
  layer: 'child' | 'host'
  resourceId?: string
  resourceType?: 'page' | 'artifact'
  nestingDepth?: number
}

// ─── Breadcrumb ───

export interface Breadcrumb {
  category?: string
  message?: string
  data?: Record<string, unknown>
  level?: LogLevelValue
  type?: 'default' | 'http' | 'navigation' | 'user' | 'error'
  timestamp?: number
}

// ─── Logger API (child / host side — thin PostMessage sender) ───

export interface LoggerApi {
  debug: (message: string, data?: Record<string, unknown>) => void
  info: (message: string, data?: Record<string, unknown>) => void
  warn: (message: string, data?: Record<string, unknown>) => void
  error: (message: string, data?: Record<string, unknown>) => void

  captureException: (error: unknown, extra?: Record<string, unknown>) => void
  captureMessage: (message: string, level?: LogLevelValue, extra?: Record<string, unknown>) => void
  addBreadcrumb: (breadcrumb: Breadcrumb) => void
  setContext: (name: string, data: Record<string, unknown> | null) => void
  setTag: (key: string, value: string | null) => void
}

// ─── CmdLog Payload (PostMessage wire format) ───

export type CmdLogSubAction = 'log' | 'captureException' | 'captureMessage' | 'addBreadcrumb' | 'setContext' | 'setTag'

export interface CmdLogPayload {
  subAction: CmdLogSubAction
  data: Record<string, unknown>
  source: LogSource
  timestamp: number
  callerStack?: string
}

// ─── Normalized Log Entry (what transports receive) ───

export interface NormalizedLogEntry {
  subAction: 'log' | 'captureException' | 'captureMessage'
  level: LogLevelValue
  message: string
  timestamp: number
  source: LogSource
  callerStack?: string
  error?: { name: string, message: string, stack?: string }
  extra?: Record<string, unknown>
  breadcrumbs: Breadcrumb[]
  contexts: Record<string, Record<string, unknown>>
  tags: Record<string, string>
}

// ─── Transport Interface ───

export interface LogTransport {
  readonly name: string
  handle: (entry: NormalizedLogEntry) => void
  flush?: () => Promise<void>
  destroy?: () => void
}

// ─── Receiver Options ───

export interface LogReceiverOptions {
  transports: LogTransport[]
  context?: Record<string, unknown>
}

export interface LogReceiverHandle {
  destroy: () => void
}

// ─── Logger Factory Options ───

export interface CreateLoggerOptions {
  source: LogSource
  target?: Window
  messageType?: string
}

// ─── Shared Utilities ───

export function serializeError(error: unknown): { name: string, message: string, stack?: string } {
  if (error instanceof Error)
    return { name: error.name, message: error.message, stack: error.stack }
  if (typeof error === 'string')
    return { name: 'Error', message: error }
  return { name: 'Error', message: String(error) }
}

/**
 * Capture the caller's stack trace, stripping internal logger frames.
 * Returns just the caller-relevant portion of the stack, or undefined if unavailable.
 */
export function captureCallerStack(framesToSkip: number): string | undefined {
  const prev = (Error as any).stackTraceLimit
  try {
    // V8 optimization: limit stack depth to frames we need.
    // +10 gives enough caller frames for useful context (async boundaries truncate stacks).
    ;(Error as any).stackTraceLimit = framesToSkip + 10
    const err = new Error('stack-capture')
    const stack = err.stack
    if (!stack)
      return undefined
    const lines = stack.split('\n')
    // +1 accounts for the first line which is "Error: stack-capture" (not a frame)
    return lines.slice(framesToSkip + 1).join('\n')
  }
  finally {
    ;(Error as any).stackTraceLimit = prev
  }
}

export function reconstructError(error: { name: string, message: string, stack?: string }): Error {
  const err = new Error(error.message)
  err.name = error.name
  if (error.stack)
    err.stack = error.stack
  return err
}
